import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  Bell,
  BellOff,
  Check,
  Trash2,
  Calendar,
  Wallet,
  Users,
  Info,
  Loader2,
  CheckCheck,
  MoreVertical,
  Clock,
  Settings,
  ShieldCheck,
  AlertTriangle,
  Zap,
  Search,
  ChevronRight,
  X
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../../components/ui/confirm-delete-dialog';
import { enablePush, isPushConfigured } from '../../lib/push';

// Métadonnées par type de notification (icône, couleurs, libellé).
const TYPE_META = {
  event: { label: 'Événements', icon: Calendar, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  contribution: { label: 'Cotisations', icon: Wallet, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  member: { label: 'Membres', icon: Users, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  security: { label: 'Sécurité', icon: ShieldCheck, color: 'text-orange-500', bg: 'bg-orange-500/10' },
  alert: { label: 'Alertes', icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/10' },
  info: { label: 'Système', icon: Info, color: 'text-slate-500', bg: 'bg-slate-500/10' }
};

const getTypeMeta = (type) => TYPE_META[type] || TYPE_META.info;

// Catégories proposées dans les préférences (mise en sourdine).
const PREF_CATEGORIES = ['event', 'contribution', 'member', 'security', 'alert', 'info'];

const prefsKey = (uid) => `notifPrefs:${uid}`;

const toJsDate = (n) => (n.createdAt?.toDate ? n.createdAt.toDate() : new Date(n.date || n.createdAt || Date.now()));

// Horodatage relatif court : « à l'instant », « il y a 5 min », « il y a 2 h »,
// puis la date complète au-delà d'un jour.
const formatRelative = (date) => {
  const diff = Date.now() - date.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `il y a ${d} j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [enablingPush, setEnablingPush] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [mutedTypes, setMutedTypes] = useState([]);

  // Charger les préférences locales (catégories en sourdine) propres au membre.
  useEffect(() => {
    if (!user) return;
    try {
      const raw = localStorage.getItem(prefsKey(user.uid));
      if (raw) setMutedTypes(JSON.parse(raw).mutedTypes || []);
    } catch { /* préférences illisibles : on ignore */ }
  }, [user]);

  const toggleMuted = (type) => {
    setMutedTypes((prev) => {
      const next = prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type];
      try { localStorage.setItem(prefsKey(user.uid), JSON.stringify({ mutedTypes: next })); } catch { /* quota / mode privé */ }
      return next;
    });
  };

  const handleEnablePush = async () => {
    setEnablingPush(true);
    try {
      await enablePush(user.uid);
      toast.success('Notifications push activées sur cet appareil');
    } catch (e) {
      toast.error(e.message || "Impossible d'activer les notifications push");
    } finally {
      setEnablingPush(false);
    }
  };

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setNotifications(docs);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching notifications:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Notifications visibles = hors catégories mises en sourdine (préférence locale).
  const visibleNotifications = useMemo(
    () => notifications.filter((n) => !mutedTypes.includes(n.type || 'info')),
    [notifications, mutedTypes]
  );

  const filteredNotifications = useMemo(() => {
    let list = visibleNotifications;
    if (activeTab === 'unread') list = list.filter((n) => !n.read);
    else if (activeTab !== 'all') list = list.filter((n) => n.type === activeTab);

    const q = search.trim().toLowerCase();
    if (q) list = list.filter((n) => `${n.title || ''} ${n.message || ''}`.toLowerCase().includes(q));
    return list;
  }, [visibleNotifications, activeTab, search]);

  const groupedNotifications = useMemo(() => {
    const groups = {
      today: [],
      yesterday: [],
      older: []
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    filteredNotifications.forEach(n => {
      const date = n.createdAt?.toDate ? n.createdAt.toDate() : new Date(n.date || n.createdAt);
      if (date >= today) {
        groups.today.push(n);
      } else if (date >= yesterday) {
        groups.yesterday.push(n);
      } else {
        groups.older.push(n);
      }
    });

    return groups;
  }, [filteredNotifications]);

  const getIcon = (type) => getTypeMeta(type);

  const markAsRead = async (id) => {
    try {
      await updateDoc(doc(db, 'notifications', id), { read: true });
    } catch (error) {
      console.error('Error marking as read:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const markAllAsRead = async () => {
    const unread = notifications.filter(n => !n.read);
    if (unread.length === 0) return;

    try {
      const batch = writeBatch(db);
      unread.forEach(n => {
        batch.update(doc(db, 'notifications', n.id), { read: true });
      });
      await batch.commit();
      toast.success('Toutes les notifications ont été marquées comme lues');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteNotification = async (id) => {
    try {
      await deleteDoc(doc(db, 'notifications', id));
      toast.success('Notification supprimée');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const deleteAllNotifications = async () => {
    setDeletingAll(true);
    try {
      const batch = writeBatch(db);
      notifications.forEach(n => {
        batch.delete(doc(db, 'notifications', n.id));
      });
      await batch.commit();
      toast.success('Toutes les notifications ont été supprimées');
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingAll(false);
      setShowDeleteAllModal(false);
    }
  };

  const unreadCount = visibleNotifications.filter(n => !n.read).length;

  // Clic sur une notification : marque comme lue puis navigue si un lien existe.
  const handleOpen = (notification) => {
    if (!notification.read) markAsRead(notification.id);
    if (notification.link) navigate(notification.link);
  };

  const NotificationItem = ({ notification }) => {
    const { icon: Icon, color, bg } = getIcon(notification.type);
    const date = toJsDate(notification);
    const clickable = !!notification.link;

    return (
      <div
        onClick={clickable ? () => handleOpen(notification) : undefined}
        className={`group p-4 flex items-start gap-4 transition-all duration-200 hover:bg-muted/30 border-l-4 ${clickable ? 'cursor-pointer' : ''} ${!notification.read ? 'border-primary bg-primary/5' : 'border-transparent'
          }`}
      >
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${bg} shadow-sm group-hover:scale-110 transition-transform`}>
          <Icon className={`h-6 w-6 ${color}`} />
        </div>

        <div className="flex-1 min-w-0 py-1">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h4 className={`font-semibold leading-none flex items-center gap-2 ${!notification.read ? 'text-foreground' : 'text-slate-500'}`}>
              {!notification.read && <span className="inline-block h-2 w-2 rounded-full bg-primary shrink-0" aria-label="Non lu" />}
              {notification.title}
            </h4>
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1 shrink-0" title={date.toLocaleString('fr-FR')}>
              <Clock className="w-3 h-3" />
              {formatRelative(date)}
            </span>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-2">
            {notification.message}
          </p>
          <div className="flex items-center gap-4">
            {!notification.read && (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs font-bold text-primary hover:no-underline"
                onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
              >
                Marquer comme lu
              </Button>
            )}
            {clickable && (
              <span className="text-xs font-bold text-primary flex items-center gap-0.5">
                Voir <ChevronRight className="h-3.5 w-3.5" />
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center self-center opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!notification.read && (
                <DropdownMenuItem onClick={() => markAsRead(notification.id)}>
                  <Check className="h-4 w-4 mr-2" /> Marquer comme lu
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => deleteNotification(notification.id)} className="text-destructive focus:text-destructive">
                <Trash2 className="h-4 w-4 mr-2" /> Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 animate-pulse">
        <div className="w-16 h-16 rounded-3xl bg-primary/10 flex items-center justify-center mb-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
        <p className="text-slate-500 font-medium tracking-tight">Synchronisation de vos alertes...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-12" data-testid="notifications-page">
      {/* Header Section */}
      <div className="relative overflow-hidden rounded-3xl md:rounded-[2.5rem] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-5 md:p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0iI2ZmZiIgZmlsbC1vcGFjaXR5PSIuMDMiIGN4PSIyMCIgY3k9IjIwIiByPSIyIi8+PC9nPjwvc3ZnPg==')] opacity-30" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl -mr-20 -mt-20" />

        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-xs font-bold tracking-widest uppercase">
              <Zap className="h-3 w-3 text-yellow-400" />
              Notifications
            </div>
            <h1 className="text-2xl md:text-4xl font-black tracking-tight">Centre d'alertes</h1>
            <p className="text-slate-400">
              {unreadCount > 0
                ? `Il y a ${unreadCount} message(s) non lu(s) dans votre boîte de réception.`
                : "Votre boîte de réception est parfaitement à jour."}
            </p>
          </div>

          <div className="flex gap-2">
            {notifications.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="bg-white/5 border-white/10 hover:bg-white/10 text-white rounded-2xl h-11">
                    Actions <MoreVertical className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl border-slate-200 dark:border-slate-800 shadow-xl">
                  {unreadCount > 0 && (
                    <DropdownMenuItem onClick={markAllAsRead} className="py-3">
                      <CheckCheck className="h-4 w-4 mr-3 text-primary" /> Tout marquer comme lu
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setShowDeleteAllModal(true)} className="py-3 text-destructive focus:text-destructive">
                    <Trash2 className="h-4 w-4 mr-3" /> Tout supprimer
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button onClick={() => setPrefsOpen(true)} className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 rounded-2xl h-11 px-6">
              <Settings className="h-4 w-4 mr-2" /> Préférences
            </Button>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <Tabs defaultValue="all" className="w-full" onValueChange={setActiveTab}>
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
          <TabsList className="bg-slate-100 dark:bg-slate-900/50 p-1 rounded-2xl h-auto w-full md:w-auto flex justify-start overflow-x-auto no-scrollbar">
            <TabsTrigger value="all" className="rounded-xl px-4 h-11 shrink-0 whitespace-nowrap data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:shadow-sm">
              Toutes
            </TabsTrigger>
            <TabsTrigger value="unread" className="rounded-xl px-4 h-11 shrink-0 whitespace-nowrap data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 relative">
              Non lues
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white ring-2 ring-background">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="event" className="rounded-xl px-4 h-11 shrink-0 whitespace-nowrap data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800">
              Événements
            </TabsTrigger>
            <TabsTrigger value="contribution" className="rounded-xl px-4 h-11 shrink-0 whitespace-nowrap data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800">
              Cotisations
            </TabsTrigger>
          </TabsList>

          <Button variant="ghost" size="sm" className="text-slate-500 hover:bg-slate-100" onClick={markAllAsRead} disabled={unreadCount === 0}>
            Tout marquer comme lu
          </Button>
        </div>

        <TabsContent value={activeTab} className="focus-visible:outline-none focus-visible:ring-0">
          {notifications.length > 0 && (
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher une notification..."
                className="pl-11 h-12 rounded-2xl bg-white/60 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800"
                data-testid="notification-search"
              />
              {search && (
                <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          <Card className="border-slate-200 dark:border-slate-800/50 rounded-[2rem] overflow-hidden bg-white/50 dark:bg-slate-900/20 backdrop-blur-sm shadow-xl">
            <CardContent className="p-0">
              {filteredNotifications.length > 0 ? (
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {groupedNotifications.today.length > 0 && (
                    <div className="bg-slate-50/50 dark:bg-slate-800/20 px-6 py-3">
                      <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Aujourd'hui</h3>
                    </div>
                  )}
                  {groupedNotifications.today.map((n) => <NotificationItem key={n.id} notification={n} />)}

                  {groupedNotifications.yesterday.length > 0 && (
                    <div className="bg-slate-50/50 dark:bg-slate-800/20 px-6 py-3 mt-4 border-t border-slate-100 dark:border-slate-800">
                      <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Hier</h3>
                    </div>
                  )}
                  {groupedNotifications.yesterday.map((n) => <NotificationItem key={n.id} notification={n} />)}

                  {groupedNotifications.older.length > 0 && (
                    <div className="bg-slate-50/50 dark:bg-slate-800/20 px-6 py-3 mt-4 border-t border-slate-100 dark:border-slate-800">
                      <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Plus ancien</h3>
                    </div>
                  )}
                  {groupedNotifications.older.map((n) => <NotificationItem key={n.id} notification={n} />)}
                </div>
              ) : (
                <div className="py-24 text-center">
                  <div className="inline-flex w-24 h-24 rounded-[2.5rem] bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-6 scale-110 shadow-inner">
                    <Bell className="h-10 w-10 text-slate-300 dark:text-slate-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">{search ? 'Aucun résultat' : "C'est tout calme ici..."}</h3>
                  <p className="max-w-[280px] mx-auto text-slate-500 leading-relaxed">
                    {search
                      ? `Aucune notification ne correspond à « ${search} ».`
                      : `Vous n'avez aucune notification ${activeTab !== 'all' ? 'pour cette catégorie' : ''}. Revenez plus tard !`}
                  </p>
                  <Button
                    variant="outline"
                    className="mt-8 rounded-2xl px-8 border-slate-200"
                    onClick={() => { setActiveTab('all'); setSearch(''); }}
                  >
                    {search ? 'Effacer la recherche' : "Voir tout l'historique"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete All Notifications Confirmation Modal */}
      <ConfirmDeleteDialog
        open={showDeleteAllModal}
        onOpenChange={setShowDeleteAllModal}
        onConfirm={deleteAllNotifications}
        title="Supprimer toutes les notifications ?"
        description={`Vous êtes sur le point de supprimer ${notifications.length} notification(s). Cette action est irréversible.`}
        confirmText="Tout supprimer"
        loading={deletingAll}
        variant="danger"
      />

      {/* Préférences de notification */}
      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-primary" /> Préférences</DialogTitle>
            <DialogDescription>Gérez les alertes que vous souhaitez recevoir sur cet appareil.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Notifications push */}
            <div className="rounded-2xl border p-4 bg-muted/30">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><Bell className="h-5 w-5 text-primary" /></div>
                  <div>
                    <p className="font-semibold text-sm">Notifications push</p>
                    <p className="text-xs text-muted-foreground">Recevoir les alertes même hors de l'application.</p>
                  </div>
                </div>
                <Button size="sm" onClick={handleEnablePush} disabled={enablingPush || !isPushConfigured()} className="rounded-xl shrink-0">
                  {enablingPush ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Activer'}
                </Button>
              </div>
              {!isPushConfigured() && <p className="text-[11px] text-amber-600 mt-2">Le push n'est pas encore configuré côté serveur.</p>}
            </div>

            {/* Catégories : mise en sourdine */}
            <div>
              <p className="font-semibold text-sm mb-1">Catégories</p>
              <p className="text-xs text-muted-foreground mb-3">Désactivez les catégories que vous ne voulez pas voir apparaître ici.</p>
              <div className="space-y-1">
                {PREF_CATEGORIES.map((type) => {
                  const meta = getTypeMeta(type);
                  const Icon = meta.icon;
                  const enabled = !mutedTypes.includes(type);
                  return (
                    <div key={type} className="flex items-center justify-between gap-3 py-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${meta.bg}`}>
                          {enabled ? <Icon className={`h-5 w-5 ${meta.color}`} /> : <BellOff className="h-4 w-4 text-slate-400" />}
                        </div>
                        <span className="text-sm font-medium">{meta.label}</span>
                      </div>
                      <Switch checked={enabled} onCheckedChange={() => toggleMuted(type)} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setPrefsOpen(false)} className="rounded-xl">Fermer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
